import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Lock, Mail, KeyRound, Loader2, ShieldCheck, Eye, EyeOff, X, Send, CheckCircle2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { useAuth, formatApiErrorDetail } from '@/context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminLogin() {
  const { admin, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSubmitting, setForgotSubmitting] = useState(false);
  const [forgotMessage, setForgotMessage] = useState('');

  const redirectTo = location.state?.from || '/admin/orders';

  useEffect(() => {
    if (admin && admin !== false) {
      navigate(redirectTo, { replace: true });
    }
  }, [admin, navigate, redirectTo]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email || !password) {
      setError('Please enter both email and password.');
      return;
    }
    setSubmitting(true);
    try {
      await login(email.trim(), password);
      navigate(redirectTo, { replace: true });
    } catch (err) {
      const status = err.response?.status;
      const detail = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      if (status === 423) setError(detail);
      else if (status === 401) setError('Invalid email or password.');
      else setError(detail || 'Login failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const openForgot = () => {
    setForgotEmail(email);
    setForgotMessage('');
    setForgotOpen(true);
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    if (!forgotEmail) return;
    setForgotSubmitting(true);
    setForgotMessage('');
    try {
      const r = await axios.post(`${API}/auth/forgot-password`, {
        email: forgotEmail.trim(),
        origin_url: window.location.origin,
      });
      setForgotMessage(r.data?.message || 'If an admin account exists for that email, a reset link has been sent.');
    } catch (err) {
      // Even on error, show generic message to avoid email enumeration.
      setForgotMessage('If an admin account exists for that email, a reset link has been sent.');
    } finally {
      setForgotSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-[calc(100vh-160px)] flex items-center justify-center px-4 py-16"
      style={{ backgroundColor: '#f7f9f3' }}
      data-testid="admin-login-page"
    >
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-md bg-white rounded-2xl shadow-xl overflow-hidden border border-emerald-100"
      >
        <div
          className="px-8 py-6 text-white"
          style={{ background: 'linear-gradient(135deg,#2d5016 0%,#3a6b1d 100%)' }}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center">
              <ShieldCheck className="w-5 h-5" />
            </div>
            <div>
              <div className="text-sm uppercase tracking-wider opacity-80">IIY Admin</div>
              <div className="text-xl font-semibold">Sign in to continue</div>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="px-8 py-7 space-y-5" data-testid="admin-login-form">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Email</label>
            <div className="relative">
              <Mail className="absolute left-3 top-3.5 w-4 h-4 text-slate-400" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
                placeholder="admin@iiypv.com"
                disabled={submitting}
                data-testid="admin-login-email-input"
                className="w-full pl-10 pr-3 py-2.5 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-emerald-600/30 focus:border-emerald-600 transition"
              />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-sm font-medium text-slate-700">Password</label>
              <button
                type="button"
                onClick={openForgot}
                className="text-xs text-emerald-700 hover:underline font-medium"
                data-testid="admin-forgot-password-link"
              >
                Forgot password?
              </button>
            </div>
            <div className="relative">
              <KeyRound className="absolute left-3 top-3.5 w-4 h-4 text-slate-400" />
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder={showPassword ? 'Your password' : '\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022'}
                disabled={submitting}
                data-testid="admin-login-password-input"
                className="w-full pl-10 pr-10 py-2.5 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-emerald-600/30 focus:border-emerald-600 transition"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-2 top-2.5 p-1 text-slate-500 hover:text-slate-700"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                data-testid="admin-login-toggle-password"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {error && (
            <div
              className="text-sm rounded-lg px-3 py-2.5 bg-red-50 border border-red-200 text-red-700"
              data-testid="admin-login-error"
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            data-testid="admin-login-submit-button"
            className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-white font-medium transition"
            style={{
              background: submitting ? '#7f9b66' : 'linear-gradient(135deg,#2d5016 0%,#3a6b1d 100%)',
              cursor: submitting ? 'not-allowed' : 'pointer',
            }}
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
            {submitting ? 'Signing in\u2026' : 'Sign In'}
          </button>

          <p className="text-xs text-slate-500 text-center pt-2">
            Authorised personnel only. All activity is logged.
          </p>
        </form>
      </motion.div>

      <AnimatePresence>
        {forgotOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center px-4"
            onClick={() => setForgotOpen(false)}
            data-testid="admin-forgot-modal"
          >
            <motion.div
              initial={{ opacity: 0, y: 12, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 12, scale: 0.96 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden"
            >
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
                <h3 className="text-lg font-semibold text-slate-800">Reset your password</h3>
                <button
                  type="button"
                  onClick={() => setForgotOpen(false)}
                  className="p-1 text-slate-500 hover:text-slate-700"
                  aria-label="Close"
                  data-testid="admin-forgot-close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {forgotMessage ? (
                <div className="px-6 py-8 text-center" data-testid="admin-forgot-success">
                  <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto mb-3" />
                  <p className="text-sm text-slate-700 mb-2">{forgotMessage}</p>
                  <p className="text-xs text-slate-500 mb-5">Check your inbox (and spam folder). The link expires in 1 hour.</p>
                  <button
                    type="button"
                    onClick={() => setForgotOpen(false)}
                    className="px-4 py-2 rounded-lg bg-emerald-700 text-white text-sm font-medium hover:bg-emerald-800"
                    data-testid="admin-forgot-done"
                  >
                    Done
                  </button>
                </div>
              ) : (
                <form onSubmit={handleForgotSubmit} className="px-6 py-5 space-y-4" data-testid="admin-forgot-form">
                  <p className="text-sm text-slate-600">
                    Enter the admin email below. If it matches an account, we'll email a one-time reset link that's valid for 1 hour.
                  </p>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3.5 w-4 h-4 text-slate-400" />
                    <input
                      type="email"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      required
                      autoFocus
                      placeholder="admin@iiypv.com"
                      disabled={forgotSubmitting}
                      data-testid="admin-forgot-email-input"
                      className="w-full pl-10 pr-3 py-2.5 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-emerald-600/30 focus:border-emerald-600 transition"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={forgotSubmitting || !forgotEmail}
                    data-testid="admin-forgot-submit"
                    className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-white font-medium transition disabled:opacity-60"
                    style={{ background: 'linear-gradient(135deg,#2d5016 0%,#3a6b1d 100%)' }}
                  >
                    {forgotSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    {forgotSubmitting ? 'Sending\u2026' : 'Send reset link'}
                  </button>
                </form>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
